import { NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Optional,
  Output,
  Renderer2,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import { takeUntil } from 'rxjs/operators';

import { IconComponent } from '../../common/icon/icon.component';
import { ComponentVariant, Variants } from '../../common/variant/variant';
import { WindowRef } from '../../common/window-ref';
import { IconName } from '../../icons-container/icons.type';
import { RtTextInput } from '../../utils/rt-input';
import { TextareaAutosizeDirective } from './textarea-autosize.directive';

@Component({
  selector: 'rt-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.less'],
  host: {
    '[class.disabled]': 'disabled',
    '[style.width]': 'width',
    '[style.height]': 'height',
    '[class.invalid]': 'invalid',
    '[class.padding-small]': 'paddingSize === "small"',
    '[class.padding-small-x]': 'paddingSize === "small-x"',
    '[class.padding-medium]': 'paddingSize === "medium"',
    '[class.private-background]': 'hasPrivateBackground',
    '[class.has-right-padding]': 'hasRightPadding',
    '[class.with-icon]': '!!icon',
    '[class.icon-red-on-hover]': '!!icon && iconRedOnHover',
    '[class.has-font-weight-default]': 'hasFontWeightDefault',
    '[class]': 'variant',
    '[class.readonly]': 'readOnly',
  },
  providers: [
    {
      provide: RtTextInput,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => TextareaComponent),
    },
  ],
  imports: [TextareaAutosizeDirective, NgTemplateOutlet, IconComponent],
})
export class TextareaComponent extends RtTextInput implements AfterContentInit {
  public static readonly VARIANTS: Variants = ['rounded', ['rounded', 'plain']];
  @ComponentVariant(TextareaComponent.VARIANTS)
  @Input()
  public variant: string;
  @Input() public placeholder: string;
  @Input() public maxLength: number;
  @Input() public tabIndex: number;
  @Input() public charactersLimit: number;
  @Input() public width: string = 'auto';
  @Input() public height: string = 'auto';
  @Input() public invalid: boolean;
  @Input() public maxHeight: number | null = null;
  @Input() public autoFocus: boolean = false;
  @Input({ transform: booleanAttribute }) public autoFocusPreventScroll: boolean = false;
  @Input() public autoHeight: boolean = false;
  @Input() public hasPrivateBackground: boolean = false;
  @Input() public defaultHeight: number = 55;
  @Input() public rows: number = 2;
  @Input() public paddingSize: 'small-x' | 'small' | 'medium';
  @Input() public autosize: boolean;
  @Input() public autosizeMinRows: number;
  @Input() public autosizeMaxRows: number;
  @Input() public hasRightPadding: boolean = false;
  @Input() public icon: IconName | null = null;
  @Input() public iconRedOnHover: boolean = false;
  @Input() public hasFontWeightDefault: boolean = false;
  @Input() public iconLight: boolean = false;
  @Input() public overflowHidden: boolean = false;

  @Output() public iconClick: EventEmitter<void> = new EventEmitter();

  @ViewChild('textarea', { static: true }) private textarea: ElementRef<HTMLTextAreaElement>;
  @ViewChild('textareaWrap', { static: true }) private textareaWrap: ElementRef;

  constructor(
    private renderer2: Renderer2,
    private window: WindowRef,
    cdRef: ChangeDetectorRef,
    @Optional() @Self() ngControl?: NgControl,
  ) {
    super(cdRef, ngControl);
  }

  public get controlValue(): string | undefined {
    return this.ngControl?.control?.value;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.setValue(this.value!);
    }

    if (changes.autoFocus && changes.autoFocus.currentValue) {
      this.setFocus();
    }
  }

  public ngAfterContentInit(): void {
    if (this.autoFocus) {
      this.setFocus();
    }

    if (this.autoHeight) {
      this.adjustInputHeight(this.value);

      this.valueChange
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(value => this.adjustInputHeight(value));
    }
  }

  public getNativeInputElement(): HTMLTextAreaElement {
    return this.textarea.nativeElement;
  }

  public setFocus(): void {
    this.textarea.nativeElement.focus({ preventScroll: this.autoFocusPreventScroll });
  }

  public onFocus(event: UIEvent): void {
    this.focus.emit(event);
    this.renderer2.addClass(this.textareaWrap.nativeElement, 'has-focus');
  }

  public onBlur(event: UIEvent): void {
    this.blur.emit(event);
    this.renderer2.removeClass(this.textareaWrap.nativeElement, 'has-focus');
  }

  protected setValue(value: string): void {
    this.value = value;
    if (this.textarea.nativeElement.value !== value) {
      this.textarea.nativeElement.value = value || '';
    }
  }

  private adjustInputHeight(value: string | null): void {
    const computedStyle = this.window.nativeWindow.getComputedStyle(this.textarea.nativeElement);
    const topPadding = parseInt(
      computedStyle.getPropertyValue('padding-top').replace('px', ''),
      10,
    );
    const bottomPadding = parseInt(
      computedStyle.getPropertyValue('padding-bottom').replace('px', ''),
      10,
    );

    if (this.maxHeight !== null) {
      this.textarea.nativeElement.style.maxHeight = this.maxHeight + 'px';
    } else {
      this.textarea.nativeElement.style.overflow = 'hidden';
    }

    this.textarea.nativeElement.style.height = 'auto';
    const computedHeight = value
      ? Math.max(
          this.textarea.nativeElement.scrollHeight - topPadding - bottomPadding,
          this.defaultHeight,
        )
      : this.defaultHeight;
    this.textarea.nativeElement.style.height = computedHeight + 'px';
  }
}
